import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaMicrophone, FaPlay, FaTrash, FaPaperPlane, FaTimes } from 'react-icons/fa';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useProfile } from '../../context/ProfileContext';
import { useMusic } from '../../context/MusicContext';

const ShoutoutModal = ({ isOpen, onClose, programName, folderId }) => {
    const { audioRef } = useMusic();
    const { user } = useProfile();
    const [mode, setMode] = useState('idle'); // idle, recording, review, uploading
    const [progress, setProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [audioBlob, setAudioBlob] = useState(null);
    const recordButtonRef = useRef(null);
    const streamRef = useRef(null);
    const [duration, setDuration] = useState(0); // ✅ NEW: Store duration
    const startTimeRef = useRef(null); // ✅ NEW: Track start timestamp

    const mediaRecorder = useRef(null);
    const audioChunks = useRef([]);
    const timerRef = useRef(null);
    const playbackRef = useRef(null);

    const [isSuccess, setIsSuccess] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [visualLevel, setVisualLevel] = useState(0); // For the recording "dance"

    const db = getFirestore();
    const storage = getStorage();

    useEffect(() => {
        const primeMic = async () => {
            if (isOpen && (!streamRef.current || !streamRef.current.active)) {
                try {
                    streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
                    // Optional: Create a throwaway AudioContext to force gain stabilization
                    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                    const source = audioContext.createMediaStreamSource(streamRef.current);
                    source.connect(audioContext.destination);
                    setTimeout(() => audioContext.close(), 500);
                } catch (err) {
                    console.error("Priming failed:", err);
                }
            }
        };

        primeMic();
        
        // Cleanup remains the same: kill tracks only when the modal is destroyed/closed
    }, [isOpen]);

    // 2. Handle Audio Ducking
    useEffect(() => {
        if (!audioRef?.current) return;

        const originalVolume = audioRef.current.volume;

        if (isOpen && (mode === 'recording' || mode === 'review')) {
            // Lower radio to 10% so you can hear the shoutout
            audioRef.current.volume = 0.1;
        } else {
            // Restore volume when modal closes or returns to idle
            audioRef.current.volume = 1.0;
        }

        // Cleanup: Ensure volume is restored if the component unmounts
        return () => {
            if (audioRef.current) audioRef.current.volume = 1.0;
        };
    }, [mode, isOpen, audioRef]);

    // --- RECORDING LOGIC ---

    const stopRecording = useCallback(() => {
        if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
            mediaRecorder.current.stop();
            clearInterval(timerRef.current);
            setProgress(0);
        }
    }, []); // Empty deps or add timerRef/mediaRecorder if needed

    const startRecording = useCallback(async () => {
        audioChunks.current = []; 
        try {
            if (!streamRef.current || !streamRef.current.active) {
                streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
                // Give the hardware 300ms to stop "popping" and stabilize gain
                await new Promise(resolve => setTimeout(resolve, 300));
                console.log("Hardware stabilized.");
            }
            
            // Pick the most compatible type for localhost/mobile
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
                            ? 'audio/webm;codecs=opus' 
                            : 'audio/mp4';

            mediaRecorder.current = new MediaRecorder(streamRef.current, { mimeType });
            
            mediaRecorder.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    audioChunks.current.push(e.data);
                }
            };

            mediaRecorder.current.onstart = () => {
                console.log("Recorder is officially HOT");
                setMode('recording'); // Only show recording UI now
                startTimeRef.current = Date.now();
                
                let startTime = Date.now();
                if (timerRef.current) clearInterval(timerRef.current);
                
                timerRef.current = setInterval(() => {
                    const elapsed = Date.now() - startTime;
                    const percent = (elapsed / 6000) * 100;
                    if (percent >= 100) stopRecording();
                    else setProgress(percent);
                }, 50);
            };

            mediaRecorder.current.onstop = () => {

                const finalDuration = (Date.now() - startTimeRef.current) / 1000;
                setDuration(finalDuration);

                if (audioChunks.current.length > 0) {
                    const blob = new Blob(audioChunks.current, { type: mediaRecorder.current.mimeType });
                    if (blob.size > 1000) {
                        const url = URL.createObjectURL(blob);
                        setAudioBlob(blob);
                        setPreviewUrl(url);
                        setMode('review');
                    } else {
                        reset();
                    }
                }
                //stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.current.start(200); // 💡 Request data every 200ms

        } catch (err) {
            console.error("Mic access failed:", err);
            setMode('idle');
        }
    }, [stopRecording]);

    const handleStart = useCallback((e) => {
        // Only allow one trigger
        if (mode !== 'idle') return; 
        
        if (e.cancelable) e.preventDefault(); 
        console.log("Recording trigger detected...");
        startRecording();
    }, [startRecording, mode]);

    const handleEnd = useCallback((e) => {
        if (e.cancelable) e.preventDefault();
        console.log("Release detected...");
        stopRecording();
    }, [stopRecording]);


    // --- PREVIEW LOGIC ---
    const playPreview = () => {
        if (playbackRef.current) {
            playbackRef.current.currentTime = 0;
            playbackRef.current.play();
            setIsPlaying(true);

            // Reset the icon when the 6-second clip ends
            playbackRef.current.onended = () => setIsPlaying(false);
            playbackRef.current.onpause = () => setIsPlaying(false);
        }
    };

    const handleClose = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        onClose();
    };

    const reset = useCallback(() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        
        // Stop the recorder, but NOT the stream tracks
        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.stop();
        }

        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        mediaRecorder.current = null;
        audioChunks.current = [];
        setMode('idle');
        setPreviewUrl(null);
        setAudioBlob(null);
        setProgress(0);
        setIsPlaying(false);
    }, [previewUrl]);

   

    // --- SUBMISSION ---
    const handleSubmit = async () => {
        if (!audioBlob) return;
        setMode('uploading');

        try {
            // Use user.uid for the path (works for anonymous users too)
            const fileName = `shoutouts/${user.uid}/${Date.now()}.mp3`;
            const storageRef = ref(storage, fileName);
            
            const snapshot = await uploadBytes(storageRef, audioBlob);
            const url = await getDownloadURL(snapshot.ref);

            await addDoc(collection(db, "radio_shoutouts"), {
                audioURL: url,
                duration: duration,
                userId: user.uid,
                // ✅ Handle anonymous display names
                userName: user.isAnonymous ? 'Anonymous Listener' : (user.displayName || 'Fan'),
                submittedAt: serverTimestamp(),
                isScheduled: false,
                programName: programName || 'RadioActive 100',
                folderId: folderId || 'standard'
            });

            setIsSuccess(true); // 👈 Trigger the success UI
        
            // Auto-close the modal after 3 seconds
            setTimeout(() => {
                onClose();
                reset();
                setIsSuccess(false);
            }, 3000);
        } catch (err) {
            console.error("Upload Error Details:", err);
            // This will now show the REAL error in the console (e.g. Permission Denied)
            alert(`Failed to send: ${err.message}`);
            setMode('review');
        }
    };

    // 2. The Effect to attach non-passive listeners
    useEffect(() => {
        // Small timeout to ensure the DOM has painted the button after the modal opens
        const timer = setTimeout(() => {
            const btn = recordButtonRef.current;
            if (!btn) return;


            // Native listeners bypass React's passive defaults
            btn.addEventListener('touchstart', handleStart, { passive: false });
            btn.addEventListener('touchend', handleEnd, { passive: false });
            btn.addEventListener('mousedown', handleStart);
            btn.addEventListener('mouseup', handleEnd);
            btn.addEventListener('mouseleave', handleEnd);
        }, 100);

        return () => {
            clearTimeout(timer);
            const btn = recordButtonRef.current;
            if (btn) {
                btn.removeEventListener('touchstart', handleStart);
                btn.removeEventListener('touchend', handleEnd);
                btn.removeEventListener('mousedown', handleStart);
                btn.removeEventListener('mouseup', handleEnd);
                btn.removeEventListener('mouseleave', handleEnd);
            }
        };
    }, [handleStart, handleEnd, isOpen]); // Only re-run if handlers change or modal opens/closes

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-6">
            <div className="bg-white w-full max-w-sm rounded-[40px] p-8 text-center relative shadow-2xl">
                
                <button onClick={handleClose} className="absolute top-6 right-6 text-slate-300 hover:text-slate-500">
                    <FaTimes size={20} />
                </button>

                <h3 className="text-2xl font-black text-slate-800 tracking-tighter mb-8">
                    {mode === 'review' ? 'Review Shoutout' : 'Radio Shoutout'}
                </h3>

                <div className="flex flex-col items-center justify-center min-h-[200px]">
                    
                    {/* IDLE / RECORDING MODE */}
                    {(mode === 'idle' || mode === 'recording') && (
                        <div className="relative flex items-center justify-center">
                            <svg className="absolute w-48 h-48 -rotate-90">
                                <circle cx="96" cy="96" r="80" stroke="#f1f5f9" strokeWidth="8" fill="transparent" />
                                <circle cx="96" cy="96" r="80" stroke="#9333ea" strokeWidth="8" fill="transparent"
                                    strokeDasharray={502} strokeDashoffset={502 - (502 * progress) / 100}
                                    className="transition-all duration-75"
                                />
                            </svg>
                            <button
                                ref={recordButtonRef}
                                disabled={mode === 'uploading' || mode === 'review'}
                                className={`relative w-32 h-32 rounded-full flex items-center justify-center transition-all cursor-pointer touch-none select-none z-10 ${
                                    mode === 'recording' 
                                    ? 'bg-red-600 scale-110 shadow-[0_0_30px_rgba(220,38,38,0.5)] animate-pulse' 
                                    : 'bg-slate-900 hover:bg-slate-800'
                                }`}
                            >
                                <FaMicrophone className={`text-3xl pointer-events-none transition-transform ${mode === 'recording' ? 'scale-125 text-white' : 'text-white'}`} />
                            </button>
                        </div>
                    )}

                    {/* REVIEW MODE */}
                    {mode === 'review' && !isSuccess && (
                        <div className="space-y-8 w-full animate-in fade-in zoom-in duration-300">
                            <div className="relative flex flex-col items-center">
                                
                                {/* ✅ NEW: THE EQUALIZER BARS (Only visible when playing) */}
                                <div className={`flex items-end justify-center gap-1 h-12 mb-4 transition-opacity duration-300 ${isPlaying ? 'opacity-100' : 'opacity-0'}`}>
                                    <div className="w-1.5 bg-purple-600 rounded-full animate-[equalizer_0.8s_ease-in-out_infinite] h-full" />
                                    <div className="w-1.5 bg-purple-500 rounded-full animate-[equalizer_0.6s_ease-in-out_infinite_0.1s] h-4/5" />
                                    <div className="w-1.5 bg-purple-400 rounded-full animate-[equalizer_1s_ease-in-out_infinite_0.2s] h-3/5" />
                                    <div className="w-1.5 bg-purple-500 rounded-full animate-[equalizer_0.7s_ease-in-out_infinite_0.3s] h-4/5" />
                                    <div className="w-1.5 bg-purple-600 rounded-full animate-[equalizer_0.9s_ease-in-out_infinite_0.4s] h-full" />
                                </div>

                                {/* Play/Stop Button */}
                                <button 
                                    onClick={playPreview}
                                    className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto transition-all shadow-lg ${
                                        isPlaying ? 'bg-purple-600 text-white scale-110' : 'bg-purple-100 text-purple-600'
                                    }`}
                                >
                                    {isPlaying ? (
                                        <FaMicrophone className="text-2xl animate-pulse" /> 
                                    ) : (
                                        <FaPlay className="ml-1 text-2xl" />
                                    )}
                                </button>
                            </div>

                            <audio ref={playbackRef} src={previewUrl} className="hidden" />
                            
                            <div className="flex gap-4">
                                <button onClick={reset} className="flex-1 bg-slate-100 text-slate-600 py-4 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-2 hover:bg-red-50 hover:text-red-600 transition-all">
                                    <FaTrash /> Redo
                                </button>
                                <button onClick={handleSubmit} className="flex-1 bg-purple-600 text-white py-4 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-2 shadow-lg shadow-purple-100 hover:bg-purple-700 transition-all">
                                    <FaPaperPlane /> Send
                                </button>
                            </div>
                        </div>
                    )}

                    {/* UPLOADING MODE */}
                    {mode === 'uploading' && (
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                            <p className="text-xs font-black uppercase text-slate-400">Broadcasting...</p>
                        </div>
                    )}

                    {/* SUCCESS VIEW */}
                    {isSuccess && (
                        <div className="flex flex-col items-center justify-center min-h-[300px] animate-in zoom-in duration-500">
                            <div className="w-24 h-24 bg-purple-600 rounded-full flex items-center justify-center mb-6 shadow-2xl shadow-purple-200">
                                <FaPaperPlane className="text-white text-3xl animate-bounce" />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 tracking-tighter">Sent to Air!</h3>
                            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2 px-8">
                                Your shoutout is in the queue and will broadcast shortly.
                            </p>
                            
                            {/* Progress bar that closes the modal */}
                            <div className="w-32 h-1 bg-slate-100 mt-8 rounded-full overflow-hidden">
                                <div className="h-full bg-purple-600 animate-shrink" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-8">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                        {mode === 'recording' ? 'Recording Now...' : mode === 'review' ? 'Sounds good?' : 'Hold to Record (6s max)'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ShoutoutModal;